import { useHeaderHeight } from '@react-navigation/elements';
import { BottomButtonsLayout, theme } from 'aimo-ui';
import VehicleFormButtons from 'components/account/vehicle/VehicleFormButtons';
import VehicleFormDetails from 'components/account/vehicle/VehicleFormDetails';
import VehicleFormExtras from 'components/account/vehicle/VehicleFormExtras';
import VehicleFormTabs, {
  vehicleTabsValue,
} from 'components/account/vehicle/VehicleFormTabs';
import AccountContext from 'context/AccountContext';
import React, { ReactNode, useContext, useState } from 'react';
import { Keyboard, KeyboardAvoidingView, Platform } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useVehicleStore } from 'stores/vehicleStore';
import styled from 'styled-components/native';
import { ScreenWrapper, ScreenWrapperPressable } from 'styles/ContainerStyles';
import { isWeb } from 'utils/deviceUtils';
import { getPermitsAmount } from 'utils/permitUtils';
import { validateLicensePlate } from 'utils/vehicleUtils';

export type VehicleFormProps = {
  isNew: boolean;
  isOnboarding?: boolean;
  onSubmit: () => void;
  onDelete?: () => void;
  loading?: boolean;
  hasUnsavedChanges?: boolean;
};

type KeyboardAvoidProps = {
  children?: ReactNode;
};

const ButtonsWrapper = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: ${theme.colors.white};
  padding: ${theme.normalising.heightPixel(17)}px 20px 0;
`;

const AvoidKeyboard = ({ children }: KeyboardAvoidProps) => {
  const headerHeight = useHeaderHeight();

  if (isWeb) {
    return (
      <ScreenWrapper tabBarHeight={headerHeight} padding={0}>
        {children}
      </ScreenWrapper>
    );
  }
  return (
    <KeyboardAvoidingView
      behavior="position"
      enabled
      keyboardVerticalOffset={Platform.select({ ios: -30, android: -110 })}>
      {children}
    </KeyboardAvoidingView>
  );
};

const VehicleForm = ({
  isNew,
  isOnboarding = false,
  onSubmit,
  onDelete,
  loading,
  hasUnsavedChanges,
}: VehicleFormProps) => {
  const { vehicleData } = useVehicleStore((state) => state);
  const [selectedVehicleTab, setSelectedVehicleTab] =
    useState<vehicleTabsValue>('DETAILS');
  const [showLicensePlateError, setShowLicensePlateError] = useState(false);

  const {
    customer: { permits },
  } = useContext(AccountContext);

  const permitsAmount = getPermitsAmount(
    permits,
    vehicleData.licensePlate?.plateText
  );

  const dismissHandler = () => {
    Keyboard.dismiss();
  };

  const handleSubmit = () => {
    if (!validateLicensePlate(vehicleData.licensePlate?.plateText)) {
      setSelectedVehicleTab('DETAILS');
      setShowLicensePlateError(true);
      return;
    }
    setShowLicensePlateError(false);
    onSubmit();
  };

  return (
    <BottomButtonsLayout
      contentBottom={
        <ButtonsWrapper>
          <VehicleFormButtons
            isNew={isNew}
            loading={loading}
            hasUnsavedChanges={hasUnsavedChanges}
            onDelete={onDelete}
            handleSubmit={handleSubmit}
          />
        </ButtonsWrapper>
      }>
      <AvoidKeyboard>
        <ScrollView
          scrollIndicatorInsets={{ right: 1 }}
          scrollEventThrottle={0}>
          <ScreenWrapperPressable
            onPress={dismissHandler}
            tabBarHeight={15}
            accessible={false}>
            <VehicleFormTabs
              selectedVehicleTab={selectedVehicleTab}
              handleOnSelectedTab={setSelectedVehicleTab}
            />
            {selectedVehicleTab === 'DETAILS' && (
              <VehicleFormDetails
                showLicensePlateError={showLicensePlateError}
                permitsAmount={permitsAmount}
                isOnboarding={isOnboarding}
              />
            )}
            {selectedVehicleTab === 'EXTRAS' && <VehicleFormExtras />}
          </ScreenWrapperPressable>
        </ScrollView>
      </AvoidKeyboard>
    </BottomButtonsLayout>
  );
};

export default VehicleForm;
